import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { pb, type User } from '@/lib/pocketbase'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  checkAuth: () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const authData = await pb.collection('users').authWithPassword(email, password)
          const user = authData.record as unknown as User

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          return true
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Login failed'
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          })
          return false
        }
      },

      logout: () => {
        pb.authStore.clear()
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },

      checkAuth: () => {
        const isValid = pb.authStore.isValid
        const model = pb.authStore.model

        if (isValid && model) {
          set({
            user: model as unknown as User,
            isAuthenticated: true,
            isLoading: false,
          })
        } else {
          // Don't call pb.authStore.clear() here as it triggers onChange recursively
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'gwiz-auth',
      partialize: () => ({
        // Only persist what PocketBase needs to restore session
        // The actual auth token is managed by PocketBase's authStore
      }),
    }
  )
)

// Initialize auth state from PocketBase on load
// Using setTimeout to ensure store is fully initialized
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useAuthStore.getState().checkAuth()
  }, 0)
}
